import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useRef,
  useState,
} from "react";
import { getTabsConfiguration } from "Services/BloodLisResultData";
import { getAllData } from "Services/BloodReferenceTest/index";

/* =======================
   Types
======================= */
export interface FilterDataI {
  tabId: number;
  pageSize: number;
  pageNumber: number;
  sortColumn: string;
  sortDirection: string;
  filters: any[];
}

export interface DataI {
  gridHeaders: any[];
  gridData: any[];
}

export interface SelectedBoxI {
  // requisitionId: any[];
  LISResultDataId: any[];
}

type Context = {
  data: DataI;
  setData: React.Dispatch<React.SetStateAction<DataI>>;
  filterData: FilterDataI;
  setFilterData: React.Dispatch<React.SetStateAction<FilterDataI>>;
  searchValue: any;
  setSearchValue: React.Dispatch<React.SetStateAction<any>>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  apiCalls: () => Promise<void>;
  setCheckedAll: React.Dispatch<React.SetStateAction<boolean>>;
  checkedAll: boolean;
  loadGridData: (shouldPageLoad?: boolean) => Promise<any>;
  value: number;
  setValue: React.Dispatch<React.SetStateAction<number>>;
  selectedBox: SelectedBoxI;
  setSelectedBox: React.Dispatch<React.SetStateAction<SelectedBoxI>>;
  total: number;
  setTotal: React.Dispatch<React.SetStateAction<number>>;
};

/* =======================
   Context
======================= */
const context = createContext<Context>({} as Context);

/* =======================
   Initial Values
======================= */
export const initialFilterData: FilterDataI = {
  tabId: 1,
  pageSize: 50,
  pageNumber: 1,
  sortColumn: "",
  sortDirection: "",
  filters: [],
};

/* =======================
   Provider
======================= */
export default function BloodReferenceTestContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [data, setData] = useState<DataI>({
    gridHeaders: [],
    gridData: [],
  });
  const [filterData, setFilterData] = useState<FilterDataI>(initialFilterData);
  const [searchValue, setSearchValue] = useState<any>({});
  const [selectedBox, setSelectedBox] = useState<SelectedBoxI>({
    LISResultDataId: [],
  });

  const [checkedAll, setCheckedAll] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [value, setValue] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const isFirstLoad = useRef(true);

  /* =======================
     Load Tabs (Internal)
  ======================== */
  const getTabs = async (): Promise<void> => {
    try {
      const response = await getTabsConfiguration();
      const tabs = response?.data?.data ?? [];
      const sortedTabs = [...tabs].sort(
        (a: any, b: any) => a.sortOrder - b.sortOrder
      );

      setData((prevVal: any) => ({
        ...prevVal,
        gridHeaders: sortedTabs,
      }));

      if (isFirstLoad.current && sortedTabs.length > 0) {
        setValue(sortedTabs[0].sortOrder - 1);
        isFirstLoad.current = false;
      }
    } catch (error) {
      console.error("Failed to load tab configuration", error);
      setData((prevVal: any) => ({
        ...prevVal,
        gridHeaders: [],
      }));
    }
  };

  /* =======================
     Load Grid Data
  ======================== */
  const loadGridData = async (shouldPageLoad = true): Promise<any> => {
    setLoading(shouldPageLoad);

    // Clean filter values (trim strings)
    const cleanedFiltersData = filterData?.filters.map((item: any) => ({
      ...item,
      filterValue:
        typeof item.filterValue === "string"
          ? item.filterValue.trim()
          : item.filterValue,
    }));

    const searchParams = {
      tabId: filterData?.tabId,
      pageNumber: filterData?.pageNumber,
      pageSize: filterData?.pageSize,
      sortColumn: filterData.sortColumn,
      sortDirection: filterData.sortDirection,
      filters: cleanedFiltersData,
    };

    try {
      const response = await getAllData(searchParams);

      setData((prevVal: any) => ({
        ...prevVal,
        gridData: response.data.data,
      }));

      setTotal(response.data.total);

      return response;
    } catch (error) {
      console.error("Error loading grid data:", error);

      // Clear the grid data on error
      setData((prevVal: any) => ({
        ...prevVal,
        gridData: [],
      }));

      setTotal(0);
      return error;
    } finally {
      setLoading(false);
    }
  };

  /* =======================
     Public API Calls
  ======================== */
  const apiCalls = async (): Promise<void> => {
    await getTabs();
    await loadGridData();
  };

  return (
    <context.Provider
      value={{
        data,
        setData,
        filterData,
        setFilterData,
        searchValue,
        setSearchValue,
        loading,
        setLoading,
        setCheckedAll,
        checkedAll,
        value,
        setValue,
        selectedBox,
        setSelectedBox,
        total,
        setTotal,
        apiCalls,
        loadGridData,
      }}
    >
      {children}
    </context.Provider>
  );
}

/* =======================
   Hook
======================= */
export const useBloodReferenceTestContext = () => useContext(context);
